/* eslint-disable react/no-unused-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { FocusService } from '@pattern-library/utils';
import '@pattern-library/utils/dist/polyfills/closest';
import { setSort } from '../stateReducer/actions';
import { DataGridContext } from '../DataGrid/context';
import { sortRows } from '../utils/data-management';
import { tableColumnPropType, tableCategoriesPropType, tableResponsiveLayoutPropType } from '../utils/propTypes';
import TableBody from './TableBody';
import TableHeader from './TableHeader';
import TableFooter from './TableFooter';
import ResponsiveTable from './ResponsiveTable';
const propTypes = {
  /**
   * Array of category objects for grouping rows. If provided, you must also
   * include a hidden column for the category value.
   */
  categories: PropTypes.arrayOf(tableCategoriesPropType),
  /**
   * Array of objects that define column groupings.
   * Objects should include a `label` and `groupId` key.
   * A column definition must include a `groupId` key to be included in a group.
   */
  columnGroupings: PropTypes.array,
  /**
   * Column definitions
   *
   * __Note__: If you are using a `<Table>` with a `<DataGrid>`
   * component, this prop should be passed to the `<DataGrid>` instead.
   */
  columns: PropTypes.arrayOf(tableColumnPropType),
  /**
   * Row data. Object keys must match dataKey values in the column definition.
   * IDs are required
   *
   * __Note__: If you are using a `<Table>` with a `<DataGrid>`
   * component, this prop should be passed to the `<DataGrid>`.
   */
  data: PropTypes.arrayOf(PropTypes.object),
  /**
   * Array filter function for data provided by the DataGrid context.
   */
  dataFilter: PropTypes.func,
  /**
   * Sets the default sort order for the table
   *
   * __Note__: If you are using a `<Table>` with a `<DataGrid>`
   * component, this prop should be passed to the `<DataGrid>`.
   */
  defaultSort: PropTypes.shape({
    key: PropTypes.string.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired
  }),
  /**
   * An array of information that defines the footer content and layout
   *
   * ```
   * {
   *     colSpan: number // optional
   *     content: string,
   *     empty: boolean, // optional
   *     isLabel: boolean, // optional
   * }
   */
  footer: PropTypes.arrayOf(PropTypes.object),
  /**
   * A function called when the user selects all rows. Required for the select all checkbox to appear in the header.
   */
  onSelectAll: PropTypes.func,
  /**
   * An array of layout metadata for responsive views
   */
  responsiveLayout: PropTypes.arrayOf(tableResponsiveLayoutPropType),
  /**
   * Select all is checked
   */
  selectAll: PropTypes.bool,
  /**
   * Array of column IDs corresponding to columns where headers should be shown
   * in the sub-category row.
   */
  subCategoryHeaders: PropTypes.array,
  /**
   * A hidden description of the table for accessibility
   */
  tableCaption: PropTypes.string,
  /**
   * Rendered content to be displayed above the table
   **/
  tableHeading: PropTypes.node,
  /**
   * Object of theme-like props that will not change after the component mounts
   */
  theme: PropTypes.shape({
    /**
     * Table background color
     */
    backgroundColor: PropTypes.oneOf(['white', 'gray-05', 'gray-10']),
    /**
     * Flag to determine if the column headers are bold or regular weight
     */
    boldColumnHeaders: PropTypes.bool,
    /**
     * Determines the vertical alignment of the cells across the entire table.
     * middle: middle alignment,
     * top: top alignment
     */
    cellsAlignment: PropTypes.oneOf(['middle', 'top']),
    /**
     * Determines the vertical padding of the cells across the entire table.
     * normal: 9px top and bottom padding,
     * compact: 5px top and bottom padding,
     */
    rowSpacing: PropTypes.oneOf(['normal', 'compact']),
    /**
     * Dock the header to the top or the footer to the bottom when scrolling.
     * Must be used in conjunction with the `theme.maxHeight` prop.
     */
    fixedRows: PropTypes.oneOf(['header', 'footer', 'both', '']),
    /**
     * Maximum height of the table
     */
    maxHeight: PropTypes.string,
    /**
     * Flag to determine if the table header will be shown
     */
    showTableHeader: PropTypes.bool,
    /**
     * Flag to determine if zebra striping will be used
     */
    useZebraStriping: PropTypes.bool,
    /**
     * Determines the order of the zebra stripes
     * odd: the first row is gray (default),
     * even: the first row is white
     */
    zebraStriping: PropTypes.oneOf(['odd', 'even'])
  }),
  /**
   * Enable in order to wait for pagination to initialize before rendering.
   * Recommended when working with large datasets.
   */
  waitForPagination: PropTypes.bool
};
const defaultProps = {
  theme: {
    backgroundColor: 'white',
    boldColumnHeaders: true,
    cellsAlignment: 'middle',
    fixedRows: '',
    rowSpacing: 'normal',
    maxHeight: null,
    showTableHeader: true,
    useZebraStriping: false,
    zebraStriping: 'odd'
  },
  waitForPagination: false
};
class Table extends Component {
  constructor(props) {
    super(props);
    this.fixedColumnSetup = state => {
      const {
        columns,
        theme: {
          fixedRows
        }
      } = this.currentState;
      let fixedColumnOffset = 0;
      let showScrollGradient;
      let columnsWithPositions = [...columns];
      let fixedColumnIndex = columnsWithPositions.findIndex(col => col.fixed);
      const hasFixedHeader = fixedRows === 'header' || fixedRows === 'both';
      const hasRowHeader = columnsWithPositions.find(col => col.rowHeader);
      let headerHeight = 0;
      while (columnsWithPositions[fixedColumnIndex] && columnsWithPositions[fixedColumnIndex].fixed) {
        columnsWithPositions[fixedColumnIndex].left = fixedColumnOffset + 'px';
        let widthStr = columnsWithPositions[fixedColumnIndex].width;
        fixedColumnOffset = fixedColumnOffset + parseInt(widthStr.substring(0, widthStr.length - 2));
        fixedColumnIndex++;
      }
      if (fixedColumnIndex >= 0 || hasRowHeader) {
        if (!this.windowResizeHandler) {
          this.windowResizeHandler = this.resizeHandler.bind(this);
          window.addEventListener('resize', this.windowResizeHandler);
        }
        if (this.wrapperNode) {
          this.showScrollGradient();
        } else {
          this.showScrollGradient.bind(this);
          window.setTimeout(this.showScrollGradient, 1000);
        }
      }
      if (hasFixedHeader) {
        headerHeight = this.tableNode.querySelector('thead')?.clientHeight || 0;
      }
      return {
        ...state,
        headerHeight,
        hasFixedColumns: fixedColumnIndex >= 0,
        fixedColumnCount: fixedColumnIndex,
        fixedColumnOffset,
        columnsWithPositions,
        hasRowHeader,
        showScrollGradient
      };
    };
    this.showScrollGradient = () => {
      this.setState({
        showScrollGradient: this.wrapperNode?.offsetWidth < this.wrapperNode?.scrollWidth
      });
    };
    this.setFocusableElements = () => {
      let isEditable = false;
      let rows = this.tableNode ? this.tableNode.querySelectorAll('tr') : [];
      this.focusableElements = [];
      rows.forEach(row => {
        let rowFocusableElements = FocusService.getFocusableElementsIn(row);
        if (rowFocusableElements.length > 0) {
          isEditable = true;
          this.focusableElements.push(rowFocusableElements);
        }
      });
      return isEditable;
    };
    this.editableTableSetup = state => {
      let isEditable = this.setFocusableElements();
      if (isEditable) this.attachElementListeners();
      return {
        ...state,
        isEditable
      };
    };
    this.attachElementListeners = () => {
      this.focusableElements.forEach(row => {
        row.forEach(element => {
          element.addEventListener('focus', this.handleElementFocus, true);
        });
      });
    };
    this.handleKeydown = e => {
      let activeElementIndices = this.state.activeElementIndices;
      let nextElement;
      const KeyCodes = {
        END: 35,
        HOME: 36,
        UP: 38,
        DOWN: 40
      };
      e.stopPropagation();
      if (e.keyCode >= KeyCodes.END && e.keyCode <= KeyCodes.DOWN && !e.target.attributes.disableKeydownHandler) {
        if (e.keyCode === KeyCodes.END) {
          const lastRowIndex = this.focusableElements.length - 1;
          activeElementIndices.row = lastRowIndex;
          activeElementIndices.column = this.focusableElements[lastRowIndex].length - 1;
        } else if (e.keyCode === KeyCodes.HOME) {
          activeElementIndices.row = 0;
          activeElementIndices.column = 0;
        } else if (e.keyCode === KeyCodes.UP) {
          if (activeElementIndices.row > 0) {
            nextElement = this.focusableElements[activeElementIndices.row - 1][activeElementIndices.column];
            if (nextElement) {
              activeElementIndices.row = activeElementIndices.row - 1;
            }
          }
        } else if (e.keyCode === KeyCodes.DOWN) {
          if (activeElementIndices.row < this.focusableElements.length - 1) {
            nextElement = this.focusableElements[activeElementIndices.row + 1][activeElementIndices.column];
            if (nextElement) {
              activeElementIndices.row = activeElementIndices.row + 1;
            }
          }
        }
        this.setState({
          activeElementIndices: activeElementIndices
        });
        this.setFocus();
      }
      return;
    };
    this.handleElementFocus = e => {
      e.stopPropagation();
      let rowIndex;
      for (rowIndex = 0; rowIndex < this.focusableElements.length; rowIndex++) {
        let columnIndex = this.focusableElements[rowIndex].findIndex(element => element === e.target);
        if (columnIndex > -1) {
          this.setState({
            activeElementIndices: {
              row: rowIndex,
              column: columnIndex
            }
          });
          break;
        }
      }
      if (this.currentState.hasFixedColumns || this.currentState.theme.fixedRows) {
        this.scrollToViewElement(e.target);
      }
    };
    /**
     * When a focusable element is focused, scroll the element into view.
     * Note: this method is only called when fixed rows or columns are present.
     *
     * @param {Element} target
     */
    this.scrollToViewElement = target => {
      const cell = target.closest('td') || target.closest('th');
      // Note: when there is a fixed (sticky) header row, we need to
      // consider the height of the header row so that the element scrolled
      // into view is not hidden behind the header.
      const headerHeight = this.currentState.headerHeight;
      const viewableArea = this.getViewableArea({
        headerHeight
      });
      const cellPosition = {
        top: cell.offsetTop,
        right: cell.offsetLeft + cell.clientWidth,
        bottom: cell.offsetTop + cell.clientHeight,
        left: cell.offsetLeft
      };
      if (cellPosition.bottom < viewableArea.top) {
        this.wrapperNode.scrollTop = cellPosition.top - headerHeight;
      } else if (cellPosition.top > viewableArea.bottom) {
        this.wrapperNode.scrollTop = this.wrapperNode.scrollTop + cell.clientHeight;
      }
      if (cellPosition.left < viewableArea.left) {
        this.wrapperNode.scrollLeft = cellPosition.left - this.currentState.fixedColumnOffset;
      } else if (cellPosition.right > viewableArea.right) {
        this.wrapperNode.scrollLeft = cellPosition.right - this.wrapperNode.clientWidth;
      }
    };
    this.setFocus = () => {
      this.focusableElements[this.currentState.activeElementIndices.row][this.currentState.activeElementIndices.column].focus();
    };
    this.getViewableArea = _ref => {
      let {
        headerHeight
      } = _ref;
      const footer = this.tableNode.querySelector('.asc-table-component-footer');
      return {
        top: this.wrapperNode.scrollTop + headerHeight,
        right: this.wrapperNode.scrollLeft + this.wrapperNode.clientWidth,
        bottom: this.wrapperNode.scrollTop + this.wrapperNode.clientHeight - (footer ? footer.clientHeight : 0),
        left: this.currentState.fixedColumnOffset + this.wrapperNode.scrollLeft
      };
    };
    /**
     * This method is only called if row categories are present in the table
     */
    this.handleScroll = () => {
      const headerHeight = this.tableNode.querySelector('thead')?.clientHeight;
      // why/how would the header height change?
      if (headerHeight !== this.currentState.headerHeight) {
        this.setState({
          headerHeight
        });
      }
      this.wrapperNode.removeEventListener('scroll', this.handleScroll);
    };
    this.handleSortChange = (dataKey, nextSortOrder) => {
      const nextState = {
        key: dataKey,
        order: nextSortOrder
      };
      if (this.context) {
        const {
          dispatch
        } = this.context;
        dispatch(setSort(nextState));
      } else {
        this.setState({
          activeSort: nextState
        });
      }
      this.setState({
        activeElementIndices: {
          row: 0,
          column: 0
        }
      });
    };
    this.state = {
      isEditable: false,
      hasFixedColumns: false,
      fixedColumnOffset: '0px',
      fixedColumnCount: 0,
      activeElementIndices: {
        row: 0,
        column: 0
      },
      activeSort: this.context ? undefined : this.props.defaultSort,
      responsiveView: undefined
    };
  }
  get currentState() {
    const theme = {
      ...defaultProps.theme,
      ...this.props.theme
    };
    const dataFilter = this.props.dataFilter;
    if (!this.context) {
      return {
        ...this.props,
        ...this.state,
        theme
      };
    }
    const {
      categories,
      columns,
      pagination,
      showEmpty,
      sort: activeSort,
      visibleRows
    } = this.context;
    const result = {
      ...this.props,
      ...this.state,
      activeSort,
      categories,
      columns,
      data: dataFilter ? visibleRows.filter(dataFilter) : visibleRows,
      pagination,
      showEmpty,
      theme
    };
    return result;
  }
  componentDidMount() {
    this.previousContext = this.context || {};
    this.tableSetup();
    const {
      responsiveLayout
    } = this.currentState;
    if (responsiveLayout) {
      this.setResponsiveView(responsiveLayout);
      if (!this.windowResizeHandler) {
        this.windowResizeHandler = this.resizeHandler.bind(this);
        window.addEventListener('resize', this.windowResizeHandler);
      }
    }
  }
  componentDidUpdate(prevProps) {
    const {
      columns,
      data,
      waitForPagination
    } = this.props;
    const {
      pagination
    } = this.context || {};
    if (prevProps.columns !== columns || prevProps.data !== data || waitForPagination && pagination !== this.previousContext.pagination) {
      this.tableSetup();
    }
    this.previousContext = this.context;
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.windowResizeHandler);
  }
  resizeHandler() {
    const {
      responsiveLayout,
      hasFixedColumns,
      hasRowHeader
    } = this.currentState;
    if (responsiveLayout) {
      this.setResponsiveView(responsiveLayout);
    }
    if (hasFixedColumns || hasRowHeader) {
      this.showScrollGradient();
    }
  }
  setResponsiveView(responsiveLayout) {
    const sortedLayouts = responsiveLayout.sort((a, b) => a.maxWidth - b.maxWidth);
    const windowWidth = window.innerWidth;
    const responsiveView = sortedLayouts.find(layout => layout.maxWidth >= windowWidth);
    this.setState({
      responsiveView
    });
  }
  tableSetup() {
    const {
      data,
      pagination,
      responsiveView,
      showEmpty,
      waitForPagination
    } = this.currentState;
    if (data.length === 0 && showEmpty || waitForPagination && !pagination) {
      return;
    }
    if (!responsiveView) {
      let newState = {};
      newState = this.editableTableSetup(newState);
      newState = this.fixedColumnSetup(newState);
      if (this.wrapperNode && this.currentState.categories) {
        this.wrapperNode.addEventListener('scroll', this.handleScroll);
      }
      this.setState(newState);
    }
  }
  sortData(rows) {
    const {
      data,
      columns,
      sortTypes,
      categories,
      activeSort
    } = this.currentState;
    if (this.context) {
      return rows;
    }
    const sortedRows = sortRows(data, activeSort, columns, sortTypes, categories);
    return sortedRows;
  }
  render() {
    const {
      activeSort,
      categories,
      columnGroupings,
      columns,
      columnsWithPositions,
      data,
      fixedColumnCount,
      footer,
      hasFixedColumns,
      hasRowHeader,
      headerHeight,
      isEditable,
      onSelectAll,
      pagination,
      responsiveView,
      selectAll,
      showEmpty,
      showScrollGradient,
      subCategoryHeaders,
      tableCaption,
      tableHeading,
      theme,
      theme: {
        backgroundColor,
        fixedRows,
        maxHeight,
        showTableHeader
      },
      waitForPagination
    } = this.currentState;
    const rows = this.sortData(data);
    if (isEditable) {
      this.setFocusableElements();
    }
    if (rows.length === 0 && showEmpty || waitForPagination && !pagination) {
      return null;
    }
    const tableClassNames = cx('asc-table-component', `asc-table--bg-${backgroundColor}`, {
      'asc-table-component-editable': isEditable,
      'asc-table-component-categories': categories
    });
    const scrollClassNames = cx('asc-table-component-wrapper', `asc-table--bg-${backgroundColor}`, {
      'asc-table-component-scroll-x': hasFixedColumns || hasRowHeader,
      'asc-table-component-scroll-y': fixedRows || maxHeight
    });
    const tableOutput = /*#__PURE__*/React.createElement("table", {
      // eslint-disable-line jsx-a11y/no-noninteractive-element-interactions
      className: tableClassNames,
      ref: node => {
        this.tableNode = node;
      },
      onKeyDown: this.handleKeydown
    }, tableCaption && /*#__PURE__*/React.createElement("caption", {
      className: "asc-acc-hidden"
    }, tableCaption), showTableHeader && /*#__PURE__*/React.createElement(TableHeader, {
      columns: columnsWithPositions ? columnsWithPositions : columns,
      columnGroupings: columnGroupings,
      fixedColumnCount: fixedColumnCount,
      sortState: activeSort,
      onSortChange: this.handleSortChange,
      theme: theme,
      onSelectAll: onSelectAll,
      selectAll: selectAll
    }), /*#__PURE__*/React.createElement(TableBody, {
      categories: categories,
      columns: columnsWithPositions ? columnsWithPositions : columns,
      data: rows,
      fixedColumnCount: fixedColumnCount,
      headerHeight: headerHeight,
      theme: theme,
      subCategoryHeaders: subCategoryHeaders
    }), footer && /*#__PURE__*/React.createElement(TableFooter, {
      footer: footer,
      columns: columnsWithPositions ? columnsWithPositions : columns,
      fixedColumnCount: fixedColumnCount,
      theme: theme
    }));
    return /*#__PURE__*/React.createElement(React.Fragment, null, tableHeading, responsiveView ? /*#__PURE__*/React.createElement(ResponsiveTable, {
      categories: categories,
      tableCaption: tableCaption,
      columns: columns,
      data: rows,
      responsiveView: responsiveView,
      footer: footer,
      theme: theme,
      fixedRows: fixedRows
    }) : hasFixedColumns || fixedRows || hasRowHeader || maxHeight ? /*#__PURE__*/React.createElement("div", {
      className: cx('asc-table-component-scroll-gradient', {
        'asc-table-component-scroll-x-gradient': showScrollGradient
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: scrollClassNames,
      ref: node => {
        this.wrapperNode = node;
      },
      style: {
        maxHeight
      }
    }, tableOutput)) : tableOutput);
  }
}
Table.propTypes = propTypes;
Table.defaultProps = defaultProps;
Table.contextType = DataGridContext;
export default Table;